import type { EntityKind } from '@/api/base-types';
import { entityPath, useArchivePreview, useBaseMutation } from '@/api/base';
import { api } from '@/api/client';
import { Button } from '@/components/kit/Button';
import { Modal } from '@/components/kit/Modal';
import { Skeleton } from '@/components/kit/Note';
import { useToast } from '@/components/kit/Toast';
import { errorText } from './useUndoable';

const TITLE: Record<EntityKind, string> = {
  client: 'Отправить клиента в архив?',
  studio: 'Отправить студию в архив?',
  team: 'Отправить команду в архив?',
};
const DONE: Record<EntityKind, string> = {
  client: 'Клиент в архиве',
  studio: 'Студия в архиве',
  team: 'Команда в архиве',
};

/**
 * В архив (БЛ 4, ТЗ M2 6.5): предпросмотр того, что будет убрано,
 * приходит с сервера. Руководителя активной команды отправить нельзя —
 * диалог объясняет почему и предлагает только «Понятно».
 */
export function ArchiveModal({
  entity,
  id,
  name,
  onClose,
}: {
  entity: EntityKind;
  id: string;
  name: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const preview = useArchivePreview(entity, id);
  const archive = useBaseMutation(() => api.post(`${entityPath(entity, id)}/archive`));
  const p = preview.data;

  if (p?.blockedReason) {
    return (
      <Modal title="Отправить в архив нельзя" onClose={onClose} footer={<Button onClick={onClose}>Понятно</Button>}>
        <b>{name}</b>: {p.blockedReason}
      </Modal>
    );
  }

  if (p?.blockedBy.length) {
    const many = p.blockedBy.length > 1;
    return (
      <Modal title="Отправить в архив нельзя" onClose={onClose} footer={<Button onClick={onClose}>Понятно</Button>}>
        <b>{name}</b> — руководитель {many ? 'команд' : 'команды'} {p.blockedBy.map((t) => `«${t.name}»`).join(', ')}.
        Сначала назначьте {many ? 'этим командам' : 'команде'} другого руководителя.
      </Modal>
    );
  }

  const submit = () =>
    archive.mutate(undefined, {
      onSuccess: () => {
        toast.show(DONE[entity]);
        onClose();
      },
      onError: (e) => toast.showError(errorText(e)),
    });

  return (
    <Modal
      title={TITLE[entity]}
      onClose={onClose}
      confirm={{ label: 'Отправить в архив', onClick: submit, disabled: !p || archive.isPending }}
    >
      {!p ? (
        <Skeleton width="80%" />
      ) : entity === 'client' ? (
        <>
          <b>{name}</b> пропадёт из списков и поиска.
          {p.effects.length ? (
            <>
              {' '}
              Его действующие связи будут убраны:
              <ul className="ul">
                {p.effects.map((e) => (
                  <li key={e.text} className={e.warn ? 'wtx' : undefined}>
                    {e.text}
                  </li>
                ))}
              </ul>
            </>
          ) : null}{' '}
          История и лента сохранятся. Вернуть клиента можно в любой момент, связи — заново.
        </>
      ) : entity === 'team' ? (
        <>
          Команда <b>«{name}»</b> пропадёт из списков. Состав ({p.membersCount ?? 0} в составе) и история
          сохранятся, вернуть команду можно в любой момент.
        </>
      ) : (
        <>
          Студия <b>«{name}»</b> пропадёт из списков.{' '}
          {p.effects.length ? (
            <ul className="ul">
              {p.effects.map((e) => (
                <li key={e.text}>{e.text}</li>
              ))}
            </ul>
          ) : (
            'Команд у студии нет.'
          )}
        </>
      )}
    </Modal>
  );
}
